import React, { useEffect, useState, useCallback } from 'react';
import { trainingsAPI } from '../services/api';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Checkbox } from './ui/checkbox';
import { GraduationCap, ListChecks, AlertTriangle, X, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';

const ytEmbed = (url) => {
  if (!url) return null;
  const m = url.match(/(?:youtube\.com\/(?:watch\?v=|embed\/)|youtu\.be\/)([\w-]{11})/);
  if (m) return `https://www.youtube.com/embed/${m[1]}`;
  return url;
};

/**
 * Global training modal — mounted once in Layout, listens to window event
 * `openTraining` dispatched by <TrainingHint> anywhere in the app.
 */
const TrainingHintModal = () => {
  const [training, setTraining] = useState(null);
  const [checklistDone, setChecklistDone] = useState([]);
  const [busy, setBusy] = useState(false);

  const open = useCallback((ev) => {
    const t = ev?.detail;
    if (!t?.id) return;
    // Refetch full training (with progress) to ensure fresh state
    trainingsAPI.get(t.id).then(r => {
      setTraining(r.data);
      setChecklistDone(r.data?.progress?.checklist_done || []);
    }).catch(() => setTraining(t));
  }, []);

  useEffect(() => {
    window.addEventListener('openTraining', open);
    return () => window.removeEventListener('openTraining', open);
  }, [open]);

  const close = () => setTraining(null);

  const toggle = async (idx) => {
    const next = checklistDone.includes(idx) ? checklistDone.filter(x => x !== idx) : [...checklistDone, idx];
    setChecklistDone(next);
    setBusy(true);
    try { await trainingsAPI.updateProgress(training.id, { checklist_done: next }); }
    finally { setBusy(false); }
  };

  const markCompleted = async () => {
    setBusy(true);
    try {
      await trainingsAPI.updateProgress(training.id, { completed: true });
      toast.success('Oznaczono jako ukończone');
      close();
    } finally { setBusy(false); }
  };

  if (!training) return null;

  return (
    <Dialog open onOpenChange={close}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="training-hint-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <GraduationCap className="w-5 h-5 text-gold-500" />
            {training.title}
            <span className="text-xs font-normal text-muted-foreground">· szkolenie 1–2 min</span>
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          {training.description && <p className="text-sm text-navy-700">{training.description}</p>}
          {training.video_url && (
            <div className="aspect-video rounded-lg overflow-hidden bg-slate-100">
              <iframe src={ytEmbed(training.video_url)} className="w-full h-full" allowFullScreen title={training.title} />
            </div>
          )}
          {training.checklist?.length > 0 && (
            <Card>
              <CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-2"><ListChecks className="w-4 h-4" />Checklist</CardTitle></CardHeader>
              <CardContent className="space-y-1.5">
                {training.checklist.map((item, idx) => {
                  const done = checklistDone.includes(idx);
                  return (
                    <label key={idx} className="flex items-start gap-2 text-sm cursor-pointer">
                      <Checkbox checked={done} onCheckedChange={() => toggle(idx)} disabled={busy} />
                      <span className={done ? 'line-through text-muted-foreground' : 'text-navy-800'}>{item}</span>
                    </label>
                  );
                })}
              </CardContent>
            </Card>
          )}
          {training.common_mistakes?.length > 0 && (
            <Card className="border-amber-200 bg-amber-50/40">
              <CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-2 text-amber-800"><AlertTriangle className="w-4 h-4" />Częste błędy</CardTitle></CardHeader>
              <CardContent className="space-y-1 text-sm text-amber-900">
                {training.common_mistakes.map((m, i) => <div key={i}>• {m}</div>)}
              </CardContent>
            </Card>
          )}
          <div className="flex justify-between pt-2 border-t">
            <Button variant="outline" onClick={close}><X className="w-4 h-4 mr-2" />Zamknij</Button>
            {!training.progress?.completed && (
              <Button onClick={markCompleted} disabled={busy} className="bg-emerald-500 hover:bg-emerald-600 text-white" data-testid="hint-mark-completed">
                <CheckCircle2 className="w-4 h-4 mr-2" />Oznacz jako ukończone
              </Button>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default TrainingHintModal;
